import {ChangeDetectorRef, Component, effect, inject, viewChild} from '@angular/core';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {Sort} from '@angular/material/sort';
import {PageEvent} from '@angular/material/paginator';
import {ApplyVccData, MatchedVccFilter, MatchedVccModel, MatchedVccResponse} from '../../model/match-vcc';
import {ModalService} from 'src/app/shared/service/modal/modal.service';
import {ConfirmationDialogMatchingVccComnponent} from '../confirmation-dialog-matching-vcc/confirmation-dialog-matching-vcc.component';
import {ConfirmationDialogRevertMatchingVccComnponent} from '../confirmation-dialog-revert-matching-vcc/confirmation-dialog-revert-matching-vcc.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ComponentCanDeactivate} from 'src/infrastructure/guards/unsaved-changes.guard';
import {DatePipe} from '@angular/common';
import {BusinessErrorsDialog} from '../business-errors-dialog/business-errors-dialog.component';
import {MatchingVccPaymentComponent} from '../matching-vcc-payment-detail/matching-vcc-payment-detail.component';
import {MatchingVccGridService} from "../../service/matching-vcc-grid.service";
import {GridEventOutput, GridModel} from "../../../shared/model/page-grid";
import {PageGridComponent} from "../../../shared/components/page-grid/page-grid.component";
import {Merchant} from "../../../dashboard/model/merchant-guid";
import {MerchantsService} from "../../../../infrastructure/merchants/service/merchants.service";
import {MatchingVccService} from '../../service/matching-vcc.service';

@AutoUnsubscribe()
@Component({
    selector: 'matching-vcc',
    templateUrl: './matching-vcc.component.html',
    styleUrls: ['./matching-vcc.component.scss'],
    standalone: false
})
export class MatchingVccComponent implements ComponentCanDeactivate {
  private readonly pageGrid = viewChild(PageGridComponent);

  private readonly matchingVccGridService = inject(MatchingVccGridService);

  private readonly merchantService = inject(MerchantsService);

  private readonly modalService = inject(ModalService);

  private readonly snackBar = inject(MatSnackBar);

  private readonly datePipe = inject(DatePipe);

  private columnStyles = (item: MatchedVccModel) => {
    if (item?.isAmountMatches && !item?.userApproved && !item?.dateApproved && !item?.revert)
      return { 'background-color': '#D4FAE0' };
    if (item?.revert)
      return { 'background-color': '#A9DDF5' };
    if (item?.userApproved && item?.dateApproved)
      return { 'background-color': '' }
    return { 'background-color': '#FAD4D5' };
  }

  private linkStyles = (item: MatchedVccModel): Record<string, string> => {
    if (!item.businessErrors) return { cursor: 'text', 'text-decoration': 'none' };
    return { 'text-decoration': 'underline', color: '#ED6B6D' };
  }

  protected readonly gridConfig: GridModel<MatchedVccResponse> = {
    gridDataTestId: 'matching-vcc-list-title',
    gridTitle: 'Connexpay Vcc Matching Report V1',
    replayButton: true,
    isTotalsRowHidden: true,
    showActionColumn: true,
    eventButton: { text: 'Apply Update' },
    defaultSort: { active: 'id', direction: 'desc' },
    columns: [
      {
        columnProperty: 'clientName',
        columnHeader: 'Merchant Name',
        sort: true,
      },
      {
        columnProperty: 'orderNumber',
        columnHeader: 'Connexpay Trip Record Locator',
        sort: true,
        clickable: true,
        linkEventFunction: (item: MatchedVccModel) =>
          `FinanceResources/MatchingVcc/${item.connexPayAccountingDailyDetailId}?FmsBookingId=${item?.fmsBookingId ?? 0}`,
      },
      {
        columnProperty: 'cardLastFour',
        columnHeader: 'Card Last Four',
        sort: true,
      },
      {
        columnProperty: 'merchantName',
        columnHeader: 'Vcc Provider Name',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'releasedDate',
        columnHeader: 'Vcc Provider Released Date',
        sort: true,
        processData: (date: string) => this.datePipe.transform(date, 'M/d/yyyy') ?? '',
      },
      {
        columnProperty: 'connexPayAccountingDailyDetailAmount',
        columnHeader: 'Vcc Provider Total',
        sort: true,
        clickable: true,
        linkEventFunction: (item: MatchedVccModel) =>
          `FinanceResources/MatchingVccPaymentDetail/${item.connexPayAccountingDailyDetailId}?FmsBookingId=${item?.fmsBookingId ?? 0}`,
      },
      {
        columnProperty: 'adxCCAmount',
        columnHeader: 'Booking Total',
        sort: true,
      },
      {
        columnProperty: 'tramsBookingTotalFare',
        columnHeader: 'Trams Total',
        sort: true,
      },
      {
        columnProperty: 'tramsBookingCommAmt',
        columnHeader: 'Agent Commission',
        sort: true,
      },
      {
        columnProperty: 'cogs',
        columnHeader: 'Cogs',
        sort: true,
      },
      {
        columnProperty: 'adxTripReference',
        columnHeader: 'Trip Record Locator',
        sort: true,
      },
      {
        columnProperty: 'tramsInvoiceNumber',
        columnHeader: 'Trams Invoice Number',
        sort: true,
      },
      {
        columnProperty: 'agentName',
        columnHeader: 'Agent Name',
        sort: true,
      },
      {
        columnProperty: 'adxCardIssueDate',
        columnHeader: 'Card Issued Date',
        sort: true,
        processData: (date: string) => this.datePipe.transform(date, 'M/d/yyyy') ?? '',
      },
      {
        columnProperty: 'travelerName',
        columnHeader: 'Traveler Name',
        sort: true,
      },
      {
        columnProperty: 'tramsBookingStartDate',
        columnHeader: 'Booking Start Date',
        sort: true,
        processData: (date: string) => this.datePipe.transform(date, 'M/d/yyyy') ?? '',
      },
      {
        columnProperty: 'tramsBookingEndDate',
        columnHeader: 'Booking End Date',
        sort: true,
        processData: (date: string) => this.datePipe.transform(date, 'M/d/yyyy') ?? '',
      },
      {
        columnProperty: 'userApproved',
        columnHeader: 'User Approved',
        sort: true,
      },
      {
        columnProperty: 'dateApproved',
        columnHeader: 'Date Approved',
        sort: true,
        processData: (date: string) => this.datePipe.transform(date, 'M/d/yyyy') ?? '',
      },
      {
        columnProperty: 'varianceAmount',
        columnHeader: 'Suggested Adjustment Commission',
        sort: true,
        clickable: true,
        style: this.columnStyles,
        linkStyle: this.linkStyles,
      },
    ],
    filters: [
      {
        filterProperty: 'merchantGuid',
        filterDisplayName: 'Merchant name',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.merchantService.getMerchantsData(),
          dropdownValueProp: 'merchantGuid',
          dropdownDisplayWith: (item: Merchant) => item.fullName,
        }
      },
      {
        filterProperty: 'tramsBookingNo',
        filterDisplayName: 'Trams Booking Number',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'Booking',
          autocompleteColumnName: 'TramsBookingNo',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'releasedDateFrom',
        filterDisplayName: 'Released Date From',
        filterType: 'DATE',
      },
      {
        filterProperty: 'releasedDateTo',
        filterDisplayName: 'Released Date To',
        filterType: 'DATE',
      },
      {
        filterProperty: 'tramsBookingStartDateFrom',
        filterDisplayName: 'Booking Start Date From',
        filterType: 'DATE',
      },
      {
        filterProperty: 'tramsBookingStartDateTo',
        filterDisplayName: 'Booking Start Date To',
        filterType: 'DATE',
      },
      {
        filterProperty: 'tramsBookingEndDateFrom',
        filterDisplayName: 'Booking End Date From',
        filterType: 'DATE',
      },
      {
        filterProperty: 'tramsBookingEndDateTo',
        filterDisplayName: 'Booking End Date To',
        filterType: 'DATE',
      },
      {
        filterProperty: 'orderNumber',
        filterDisplayName: 'Connexpay Trip Record Locator',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'ConnexPayDailyDetailTramsBookingJoinModel',
          autocompleteColumnName: 'OrderNumber',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'tramsInvoiceNumber',
        filterDisplayName: 'Trams Invoice Number',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'ConnexPayDailyDetailTramsBookingJoinModel',
          autocompleteColumnName: 'TramsInvoiceNumber',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'merchantName',
        filterDisplayName: 'Vendor Name',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'Merchant',
          autocompleteColumnName: 'MerchantName',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'cardLastFour',
        filterDisplayName: 'Vcc Number',
        filterType: 'TEXT',
      },
      {
        filterProperty: 'isMapped',
        filterDisplayName: 'Mapped To Trams Data',
        filterType: 'RADIOBUTTON',
        config: {
          radioButtonBoolean: true
        },
      },
      {
        filterProperty: 'isAmountMatches',
        filterDisplayName: `Matched Vcc's`,
        filterType: 'RADIOBUTTON',
      },
      {
        filterProperty: 'isResolved',
        filterDisplayName: 'Resolved',
        filterType: 'RADIOBUTTON',
        config: {
          radioButtonBoolean: true,
        }
      },
    ],
    filtersInitial: { isMapped: true },
    gridGetService: this.matchingVccGridService
  };

  items: MatchedVccModel[] = [];
  filters: MatchedVccFilter = {};
  pagination: Partial<PageEvent & { totalItemCount?: number }> = {};
  sorting: Partial<Sort> = {};
  itemsSave: ApplyVccData[] = [];
  itemsNoSave: ApplyVccData[] = [];

  allowNavigate = true;

  constructor(private cdr: ChangeDetectorRef,
    protected matchingVccService: MatchingVccService
  ) {
    this.matchingVccService.getMatchingFilter().subscribe((filter: MatchedVccFilter) => {
      this.gridConfig.filtersInitial = {
        isMapped: true,
          ...filter
      }
    });
    effect(() => {
      const items = this.matchingVccGridService.items();
      this.items = [...items];
      this.setItemsSave();
    });
  }

  canDeactivate() {
    this.matchingVccService.setMatchingFilter({});
    return this.allowNavigate;
  }

  applyUpdate() {
    this.allowNavigate = false;

    if (this.itemsSave.length) {
      this.modalService.open(
        ConfirmationDialogMatchingVccComnponent,
        {
          data: { toSave: this.itemsSave, noSave: this.itemsNoSave },
          disableClose: true
        }
      ).afterClosed().subscribe((confirmation: boolean) => {
        if (!confirmation) return;
        this.pageGrid()?.getItems();
        this.cdr.detectChanges();
      });
    } else {
      this.snackBar.open('No items to save', 'Dismiss', {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
    }
  }

  applyRevert(element: MatchedVccModel) {
    this.allowNavigate = false;

    if (element?.revert) {
      delete element.revert;
      this.setItemsSave();
      return;
    }

    this.modalService.open(ConfirmationDialogRevertMatchingVccComnponent)
      .afterClosed().subscribe((confirmation: boolean) => {
        if (confirmation) {
          element.revert = true;
          this.setItemsSave();
        }
      });
  }

  private setItemsSave() {
    this.itemsSave = [];
    this.itemsNoSave = [];

    this.items.forEach((item) => {
      if (item?.isAmountMatches && !item?.userApproved && !item?.dateApproved && !item?.revert) {
        this.itemsSave.push({
          id: item?.connexPayAccountingDailyDetailId,
          fmsBookingId: item?.fmsBookingId,
          tramsBookingNo: item?.tramsBookingNo,
          merchantGuid: item?.merchantGuid,
          releasedDate: item?.releasedDate,
          adxTripReference: item?.adxTripReference,
          adjustedCommission: item?.varianceAmount,
          isResolved: item?.isResolved,
          isEnabled: item?.isEnabled,
        });
      } else {
        if (item?.shouldResolve) {
          this.itemsSave.push({
            id: item?.connexPayAccountingDailyDetailId,
            fmsBookingId: item?.fmsBookingId,
            tramsBookingNo: item?.tramsBookingNo,
            merchantGuid: item?.merchantGuid,
            releasedDate: item?.releasedDate,
            adxTripReference: item?.adxTripReference,
            isResolved: item?.shouldResolve,
            isEnabled: item?.isEnabled,
          });
        } else {
          this.itemsNoSave.push({
            id: item?.connexPayAccountingDailyDetailId,
            fmsBookingId: item?.fmsBookingId,
            tramsBookingNo: item?.tramsBookingNo,
            merchantGuid: item?.merchantGuid,
            releasedDate: item?.releasedDate,
            adxTripReference: item?.adxTripReference,
            adjustedCommission: item?.varianceAmount,
            isEnabled: item?.isEnabled,
          });
        }
      }
    });
  }

  setResolve(item: MatchedVccModel) {
    this.allowNavigate = false;

    if (item.shouldResolve) {
      delete item.shouldResolve;
    } else {
      item.shouldResolve = true;
    }

    this.setItemsSave();
  }

  openErrorsDialog(errors: string) {
    this.modalService.open(BusinessErrorsDialog, { data: errors });
  }

  openPaymentDetailsDialog(item: MatchedVccModel): void {
    this.modalService.open(MatchingVccPaymentComponent, {
      width: '70dvw',
      data: item,
    });
  }

  protected handleItemClick({ column, item }: GridEventOutput<MatchedVccModel>) {
    if (column === 'orderNumber')
      window.open(`FinanceResources/MatchingVcc/${item.connexPayAccountingDailyDetailId}?FmsBookingId=${item?.fmsBookingId ?? 0}`, '_blank');
    if (column === 'connexPayAccountingDailyDetailAmount')
      this.openPaymentDetailsDialog(item);
    if (column === 'varianceAmount' && item.businessErrors)
      this.openErrorsDialog(item.businessErrors);
  }
  handleToggleRecord() {
    this.setItemsSave();
  }
}
